import React, { useState, useMemo } from "react";
import EAAlert from "../../shared/EAAlert";
import "./../purchasedAssorts.scss";
import "./style.scss";
import { useTranslation } from "react-i18next";
import EASkeletonLoader from "../../shared/EASkeletonLoader";
import useFutureTickets from "./hooks/useReservation";
import { isAfter, isBefore, isEqual, startOfDay } from "date-fns";
import SingleReservation from "./SingleReservation/SingleReservation";
import PastReservation from "./PastReservation";
import EAButton from "../../shared/Buttons/EAButton";

const Reservations: React.FC = () => {
  const { t } = useTranslation();
  const [showPastReservation, setShowPastReservation] = useState(false);
  const { tickets, loading, refreshTickets } = useFutureTickets();

  // Sort tickets by their exercises' start times
  const sortedTickets = useMemo(() => {
    if (!tickets || tickets.length === 0) return [];

    // Create a copy to avoid mutating original data
    return [...tickets].map((ticket) => {
      // Sort exercises within each ticket from earliest to latest
      const sortedExercises = [...ticket.exercises].sort((a, b) => {
        // Handle cases where startTime might be undefined
        if (!a.startTime) return 1; // Undefined dates go last
        if (!b.startTime) return -1;
        return a.startTime - b.startTime;
      });

      return { ...ticket, exercises: sortedExercises };
    });
  }, [tickets]);

  if (loading) {
    return <EASkeletonLoader height="120px" />;
  }

  if (tickets.length <= 0) {
    return <EAAlert type="warning" message={t("brak_biletow")} />;
  }

  const isTodayOrLater = (dateNumber: number): boolean => {
    const today = startOfDay(new Date());
    const inputDate = startOfDay(new Date(dateNumber));

    return isEqual(inputDate, today) || isAfter(inputDate, today);
  };

  const isEarlierThanToday = (dateNumber: number): boolean => {
    const today = startOfDay(new Date());
    const inputDate = startOfDay(new Date(dateNumber));

    return isBefore(inputDate, today);
  };

  const hasValidTicket = sortedTickets.some((ticket) =>
    ticket.exercises.some((exercise) => isEarlierThanToday(exercise.startTime) && ticket.ticket === false)
  );

  // Create flat array of upcoming reservations for sorting
  const upcomingReservations = sortedTickets.flatMap((ticket) =>
    ticket.exercises
      .filter((exercise) => exercise.startTime && isTodayOrLater(exercise.startTime))
      .map((exercise) => ({ ticket, exercise }))
  );

  // Sort upcoming reservations by start time (earliest first)
  upcomingReservations.sort((a, b) => {
    return a.exercise.startTime - b.exercise.startTime;
  });

  return (
    <>
      {upcomingReservations.map((item, index) => (
        <div className="reservation_wrapper" key={`upcoming-${index}`}>
          <SingleReservation ticket={item.ticket} exercise={item.exercise} onRefreshTickets={refreshTickets} />
        </div>
      ))}

      {showPastReservation ? (
        <EAButton
          title="Schowaj historię rezerwacji"
          onClick={() => setShowPastReservation(!showPastReservation)}
          variant="text"
          color="secondary"
          fullWidth
        />
      ) : (
        <EAButton
          title={hasValidTicket ? "Pokaż historię rezerwacji" : "Brak historii rezerwacji"}
          onClick={() => setShowPastReservation(!showPastReservation)}
          variant="text"
          color="primary"
          fullWidth
          disabled={!hasValidTicket}
        />
      )}

      {showPastReservation && (
        <>
          {sortedTickets.map((ticket, ticketIndex) => (
            <ul className="reservation_wrapper__ul" key={`past-${ticketIndex}`}>
              {ticket.exercises
                .filter((exercise) => isEarlierThanToday(exercise.startTime) && ticket.ticket === false)
                .map((exercise, exerciseIndex) => (
                  <li key={`past-exercise-${exerciseIndex}`}>
                    <PastReservation ticket={ticket} exercise={exercise} onRefreshTickets={refreshTickets} />
                  </li>
                ))}
            </ul>
          ))}
        </>
      )}
    </>
  );
};

export default Reservations;
